/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styles from './MultiLineTextField.module.css';

function useDebouncedSave(nullable, value, onSave, delay = 800) {
  const lastSaved = useRef(value);
  const controller = useRef(null);
  useEffect(() => {
    if (!nullable && value === '') return;
    if (value === lastSaved.current) return;

    const timeout = setTimeout(async () => {
      controller.current?.abort();
      controller.current = new AbortController();

      try {
        await onSave(value, controller.current.signal);
        lastSaved.current = value;
      } catch (e) {
        if (e.name !== 'AbortError') console.log(e);
      }
    }, delay);
    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timeout);
  }, [value, delay, onSave]);
}

function SingleLineTextField({
  value,
  setValue,
  placeholder,
  id = 'default',
  isControlled,
  onSave = () => {
  },
  nullable = true,
  height = '35px',
  borderRadius = '5px',
  paddingLeft = '5px',
  rule,
  helperMessage,
}) {
  const [internalValue, setInternalValue] = useState('');
  const controlledValue = value || internalValue;

  useDebouncedSave(nullable, value, onSave);

  const hasHelper = !!(rule && helperMessage);
  const isRuleValid = hasHelper ? rule(controlledValue) : null;
  const tooltipId = `${id || 'input'}-helper-tooltip`;

  const ruleColor = isRuleValid ? 'var(--field-valid)' : 'var(--field-invalid)';

  return (
    <>
      <input
        className={styles.scrollableContent}
        id={id || 'input'}
        name={id}
        autoComplete="off"
        type="text"
        value={controlledValue}
        onChange={(e) => {
          if (isControlled) {
            setValue?.(e.target.value);
          } else {
            setInternalValue(e.target.value);
          }
        }}
        placeholder={placeholder || 'Enter text here ...'}
        data-tooltip-id={hasHelper ? tooltipId : undefined}
        data-tooltip-content={hasHelper ? helperMessage : undefined}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          height,
          background: 'var(--color-surface)',
          color: 'var(--color-text-secondary)',
          outline: 'none',
          borderRadius,
          fontSize: '1rem',
          paddingLeft,
          border: nullable || controlledValue?.length > 0 ? '1px solid transparent' : '1px solid var(--field-invalid)',
        }}
      />
      {hasHelper && (
        <Tooltip
          id={tooltipId}
          place="top"
          effect="solid"
          style={{ color: ruleColor }}
        />
      )}
    </>
  );
}
export default SingleLineTextField;
